import {
  ComposedChart,
  Line,
  ReferenceLine,
  Area,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
  usePlotArea,
  ZIndexLayer,
} from 'recharts';
import { round } from '../../../../../utils/util';
import s from './PerformanceGraph.module.css';
import {
  transformPerformanceData,
  buildDataSeries,
  PERFORMANCE_GRADE_THRESHOLD,
} from './utils';

// Functional constants
const PERFORMANCE_X_LABEL = 'Course Week';
const PERFORMANCE_Y_LABEL = 'Achieve Grade';

// Style constants
const COLOR_GRAY_300 = '#CBCACC'; // 100%/50% line
const COLOR_GRAY_500 = '#807F82'; // class average
const COLOR_GRAY_800 = '#31303B'; // 60% line, tick labels
const COLOR_GRAY_950 = '#0E0D16'; // Axis Labels
const COLOR_BLUE_500 = '#0600B4'; // student grade above 60%
const COLOR_RED_500 = '#E0205D'; // student grade below 60%
const GRID_FONT_SIZE = '12px';
const LEGEND_FONT_SIZE = '16px';
const PERFORMANCE_VERTICAL_STROKE_WIDTH = 1;
const PERFORMANCE_GRID_STROKE_WIDTH = 2;
const PERFORMANCE_DATA_STROKE_WIDTH = 4;
const PERFORMANCE_DOT_RADIUS = 4;
const PERFORMANCE_ACTIVE_DOT_RADIUS = 6;

// Make space for "Below X%" label
const PERFORMANCE_RIGHT_LABEL_PADDING = 72;
const PERFORMANCE_THRESHOLD_LABEL = `Below ${
  PERFORMANCE_GRADE_THRESHOLD * 100
}%`;
// Above the tooltip cursor (1100) so hover doesn't recolor the plot borders
const PLOT_BORDER_Z_INDEX = 1150;

/**
 * Left/right borders at the plot-area edges (not the data scale). That keeps
 * the XAxis right-padding gutter inside the frame for the threshold label.
 */
function PlotVerticalBorders() {
  const plotArea = usePlotArea();
  if (!plotArea) {
    return null;
  }

  const { x, y, width, height } = plotArea;
  return (
    <ZIndexLayer zIndex={PLOT_BORDER_Z_INDEX}>
      <g className="recharts-plot-vertical-borders">
        <line
          x1={x}
          y1={y}
          x2={x}
          y2={y + height}
          stroke={COLOR_GRAY_950}
          strokeWidth={PERFORMANCE_VERTICAL_STROKE_WIDTH}
        />
        <line
          x1={x + width}
          y1={y}
          x2={x + width}
          y2={y + height}
          stroke={COLOR_GRAY_950}
          strokeWidth={PERFORMANCE_VERTICAL_STROKE_WIDTH}
        />
      </g>
    </ZIndexLayer>
  );
}

function PerformanceGraph({
  studentName,
  performanceData,
  enrollmentStartDate,
  isMobile,
}) {
  const data = transformPerformanceData(performanceData, enrollmentStartDate);

  const filteredDataSeries = buildDataSeries(data);

  const formatLegendLabel = (value) => {
    if (value === 'classAverage') {
      return 'Class';
    } else if (value === 'studentGrade') {
      return 'Student';
    }
  };

  const legendItemSorter = (a) => {
    // Sort student grade to be before class average
    if (a.value === 'studentGrade') {
      return -1;
    } else if (a.value === 'classAverage') {
      return 1;
    }
  };

  // Helper function to format the grade value as a percentage
  const formatGradeValue = (value) => {
    const decimalPlaces = 2;
    const ignoreDecimalZeros = true;
    return round(value * 100, decimalPlaces, ignoreDecimalZeros) + '%';
  };

  // Helper function to validate if tooltip/cursor should show
  const shouldShowTooltip = (active, payload) => {
    // Don't show if not active or no payload
    if (!active || !payload || payload.length === 0) {
      return false;
    }

    const studentGrade = payload.find((p) => p.dataKey === 'studentGrade');
    const classAverage = payload.find((p) => p.dataKey === 'classAverage');

    // Get the actual data point from the payload
    const dataPoint = studentGrade?.payload || classAverage?.payload;
    if (!dataPoint) {
      return false;
    }

    // Don't show for week 0
    if (dataPoint.weekNumber === 0) {
      return false;
    }

    // Verify that this data point exactly matches one in our original data
    // This prevents showing during interpolation (between data points)
    const matchesDataPoint = data.some(
      (originalPoint) =>
        originalPoint.weekNumber === dataPoint.weekNumber &&
        Math.abs(
          (originalPoint.studentGrade || 0) - (dataPoint.studentGrade || 0),
        ) < 0.0001 &&
        Math.abs(
          (originalPoint.classAverage || 0) - (dataPoint.classAverage || 0),
        ) < 0.0001,
    );

    // Only show if we have valid data and it matches an actual data point
    if (!matchesDataPoint) {
      return false;
    }

    const hasValidStudentGrade =
      studentGrade &&
      studentGrade.value !== null &&
      studentGrade.value !== undefined;
    const hasValidClassAverage =
      classAverage &&
      classAverage.value !== null &&
      classAverage.value !== undefined;

    return hasValidStudentGrade || hasValidClassAverage;
  };

  const CustomTooltip = ({ active, payload, label }) => {
    // Don't show tooltip if not active or no payload
    if (!shouldShowTooltip(active, payload)) {
      return null;
    }

    const studentGrade = payload.find((p) => p.dataKey === 'studentGrade');
    const classAverage = payload.find((p) => p.dataKey === 'classAverage');

    const hasValidStudentGrade =
      studentGrade &&
      studentGrade.value !== null &&
      studentGrade.value !== undefined;
    const hasValidClassAverage =
      classAverage &&
      classAverage.value !== null &&
      classAverage.value !== undefined;

    const formatLabel = `Week ${label}`;
    return (
      <div className={s.customTooltip}>
        <span className={s.customTooltipLabel}>{`${formatLabel}`}</span>
        <div className={s.customTooltipDivider} />
        {hasValidStudentGrade && (
          <span className={s.customTooltipContent}>
            <span>{`${studentName}:`}</span>
            <span>{formatGradeValue(studentGrade.value)}</span>
          </span>
        )}
        {hasValidClassAverage && (
          <span className={s.customTooltipContent}>
            <span>Class Average:</span>
            <span>{formatGradeValue(classAverage.value)}</span>
          </span>
        )}
      </div>
    );
  };

  const customClassAverageDot = ({ payload, cx, cy }) => {
    // Don't show dot for week 0 or if there's no valid data
    if (
      !payload ||
      payload.weekNumber === 0 ||
      payload.classAverage === null ||
      payload.classAverage === undefined
    ) {
      return null;
    }

    // Don't render if coordinates are invalid (likely from null data)
    if (cx === undefined || cy === undefined || isNaN(cx) || isNaN(cy)) {
      return null;
    }

    return (
      <circle
        cx={cx}
        cy={cy}
        r={PERFORMANCE_DOT_RADIUS}
        fill={COLOR_GRAY_500}
        strokeWidth={PERFORMANCE_GRID_STROKE_WIDTH}
      />
    );
  };

  const customClassAverageActiveDot = ({ payload, cx, cy }) => {
    // Don't show dot for week 0 or if there's no valid data
    if (
      !payload ||
      payload.weekNumber === 0 ||
      payload.classAverage === null ||
      payload.classAverage === undefined
    ) {
      return null;
    }

    // Don't render if coordinates are invalid (likely from null data)
    if (cx === undefined || cy === undefined || isNaN(cx) || isNaN(cy)) {
      return null;
    }

    return (
      <circle
        cx={cx}
        cy={cy}
        r={PERFORMANCE_ACTIVE_DOT_RADIUS}
        fill="white"
        stroke={COLOR_GRAY_500}
        strokeWidth={PERFORMANCE_GRID_STROKE_WIDTH}
      />
    );
  };

  const customStudentGradeDot = (isBelowThreshold) => {
    return (props) => {
      const { cx, cy, payload } = props;

      // Don't show dot for week 0 or if there's no valid data
      if (
        !payload ||
        payload.weekNumber === 0 ||
        payload.studentGrade === null ||
        payload.studentGrade === undefined
      ) {
        return null;
      }

      // Don't render if coordinates are invalid (likely from null data)
      if (cx === undefined || cy === undefined || isNaN(cx) || isNaN(cy)) {
        return null;
      }

      // For first point after threshold change, use opposite color (trailing effect)
      // This is because we use the first point after threshold change to determine the color of the area,
      // but we want the point to be the same color as the previous point, so we don't get any clipping issues
      const effectiveIsBelowThreshold = payload.isFirstPointAfterThresholdChange
        ? !isBelowThreshold
        : isBelowThreshold;

      const fillColor = effectiveIsBelowThreshold
        ? COLOR_RED_500
        : COLOR_BLUE_500;

      return (
        <circle
          cx={cx}
          cy={cy}
          r={PERFORMANCE_DOT_RADIUS}
          fill={fillColor}
          strokeWidth={PERFORMANCE_GRID_STROKE_WIDTH}
        />
      );
    };
  };

  const createStudentGradeActiveDot = (seriesId, isBelowThreshold) => {
    return (props) => {
      const { cx, cy, payload } = props;

      // Only show the active dot if this point belongs to this series
      if (
        !payload ||
        payload.studentGrade === null ||
        payload.studentGrade === undefined ||
        payload.seriesId !== seriesId
      ) {
        return null;
      }

      // Don't show dot for week 0
      if (payload.weekNumber === 0) {
        return null;
      }

      const effectiveIsBelowThreshold = payload.isFirstPointAfterThresholdChange
        ? !isBelowThreshold
        : isBelowThreshold;

      return (
        <circle
          cx={cx}
          cy={cy}
          r={PERFORMANCE_ACTIVE_DOT_RADIUS}
          fill="white"
          stroke={effectiveIsBelowThreshold ? COLOR_RED_500 : COLOR_BLUE_500}
          strokeWidth={PERFORMANCE_GRID_STROKE_WIDTH}
        />
      );
    };
  };

  // Use the last week number to build the domain and make sure we have ticks for every week
  // We can't use the data itself because we might not have data for every week
  const finalWeekNumber =
    data.length > 0 ? data[data.length - 1].weekNumber : 0;
  const domain = [0, finalWeekNumber];

  const generateXAxisTicks = () => {
    const domainTicks = [];
    for (let i = 0; i <= finalWeekNumber; i++) {
      domainTicks.push(i);
    }
    if (isMobile) {
      return domainTicks.filter((tick) => tick % 2 === 1);
    }
    return domainTicks;
  };

  return (
    <div className={s.graphContainer}>
      <ResponsiveContainer>
        <ComposedChart
          data={data}
          margin={{ top: -20, right: 10, bottom: 20, left: 0 }}
          accessibilityLayer
          aria-label="Student Performance Graph; if using Voiceover please disable Quick Nav"
        >
          <CartesianGrid stroke={COLOR_GRAY_950} vertical={false} />
          <ReferenceLine
            y={1}
            stroke={COLOR_GRAY_300}
            strokeWidth={PERFORMANCE_GRID_STROKE_WIDTH}
          />
          <ReferenceLine
            y={0.5}
            stroke={COLOR_GRAY_300}
            strokeWidth={PERFORMANCE_GRID_STROKE_WIDTH}
          />
          <defs>
            <linearGradient id="splitFillBelow" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor={COLOR_RED_500} stopOpacity={0.2} />
              <stop offset="1" stopColor={COLOR_RED_500} stopOpacity={0.0} />
            </linearGradient>
            <linearGradient id="splitFillAbove" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor={COLOR_BLUE_500} stopOpacity={0.2} />
              <stop offset="1" stopColor={COLOR_BLUE_500} stopOpacity={0.0} />
            </linearGradient>
          </defs>
          {filteredDataSeries.map((series, index) => {
            const isBelowThreshold = series[0].isBelowThreshold;
            const seriesId = series[0].seriesId;
            return (
              <Area
                key={index}
                type="linear"
                data={series}
                dataKey="studentGrade"
                connectNulls={true}
                isAnimationActive={false}
                dot={customStudentGradeDot(isBelowThreshold)}
                activeDot={createStudentGradeActiveDot(
                  seriesId,
                  isBelowThreshold,
                )}
                stroke={isBelowThreshold ? COLOR_RED_500 : COLOR_BLUE_500}
                strokeWidth={PERFORMANCE_DATA_STROKE_WIDTH}
                legendType={index === 0 ? 'square' : 'none'} // Hide duplicate legend items
                fill={
                  isBelowThreshold
                    ? 'url(#splitFillBelow)'
                    : 'url(#splitFillAbove)'
                }
              />
            );
          })}
          <Line
            type="linear"
            dataKey="classAverage"
            stroke={COLOR_GRAY_500}
            strokeWidth={PERFORMANCE_DATA_STROKE_WIDTH}
            legendType="square"
            dot={customClassAverageDot}
            activeDot={customClassAverageActiveDot}
            connectNulls={true}
            isAnimationActive={false}
          />
          <ReferenceLine
            y={PERFORMANCE_GRADE_THRESHOLD}
            label={{
              value: PERFORMANCE_THRESHOLD_LABEL,
              position: 'insideBottomRight',
              fontSize: GRID_FONT_SIZE,
              fontStyle: 'italic',
              fill: COLOR_GRAY_800,
            }}
            stroke={COLOR_GRAY_800}
            strokeWidth={PERFORMANCE_GRID_STROKE_WIDTH}
            strokeDasharray="3 3"
          />
          <Tooltip content={CustomTooltip} />
          <Legend
            verticalAlign="top"
            wrapperStyle={{
              fontSize: LEGEND_FONT_SIZE,
              marginTop: '32px',
              marginLeft: '45px',
            }}
            formatter={(value) => {
              return formatLegendLabel(value);
            }}
            itemSorter={legendItemSorter}
            iconSize={12}
          />
          <XAxis
            dataKey="weekNumber"
            type="number"
            domain={domain}
            padding={{ right: PERFORMANCE_RIGHT_LABEL_PADDING }}
            label={{
              value: PERFORMANCE_X_LABEL,
              position: 'bottom',
              fill: COLOR_GRAY_950,
            }}
            ticks={generateXAxisTicks()}
            tickFormatter={(value) => (value === 0 ? '' : value)}
            tick={{
              fontSize: GRID_FONT_SIZE,
              fill: COLOR_GRAY_800,
            }}
            tickMargin={5}
            tickLine={false}
          />
          <YAxis
            domain={[0, 1.2]}
            label={{
              value: PERFORMANCE_Y_LABEL,
              angle: -90,
              position: 'center',
              dx: -20,
              fill: COLOR_GRAY_950,
            }}
            axisLine={false}
            interval="equidistantPreserveStart"
            ticks={[0, 0.5, 1]}
            tickFormatter={formatGradeValue}
            tick={{
              fontSize: GRID_FONT_SIZE,
              fill: COLOR_GRAY_800,
            }}
            tickMargin={-3}
            tickLine={false}
          />
          <PlotVerticalBorders />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

export default PerformanceGraph;
